import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Compromiso,
  EntidadEvento,
  Hito,
  Iniciativa,
  PropuestaDetail,
  Tag,
} from '@/types';
import {
  archiveCompromiso,
  archiveHito,
  createCompromiso,
  createHito,
  createTag,
  editHito,
  listCompromisoEventos,
  restoreCompromiso,
  restoreHito,
  updateCompromiso,
} from '@/api/clientes';
import { ConfirmArchiveModal } from './ConfirmArchiveModal';
import styles from './EditCompromisoDrawer.module.css';

interface Props {
  open: boolean;
  /** Si se pasa un compromiso, modo edición. Si no, modo creación. */
  compromiso?: Compromiso | null;
  detail: PropuestaDetail;
  tags: Tag[];
  onClose: () => void;
}

type Tab = 'datos' | 'items' | 'historial';

interface FormState {
  codigo: string;
  nombre: string;
  descripcion: string;
  iniciativaId: string;
  tagIds: string[];
}

export function EditCompromisoDrawer({
  open,
  compromiso,
  detail,
  tags,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const isEdit = !!compromiso;
  const propuestaId = detail.propuesta.id;
  const queryKey = ['propuesta-detail', propuestaId];

  const [tab, setTab] = useState<Tab>('datos');
  const [form, setForm] = useState<FormState>(emptyForm(detail.iniciativas));
  const [items, setItems] = useState<EditableItem[]>([]);
  const [archivadosVisibles, setArchivadosVisibles] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset al abrir
  useEffect(() => {
    if (!open) return;
    setError(null);
    setTab('datos');
    setArchivadosVisibles(false);
    if (compromiso) {
      setForm({
        codigo: compromiso.codigo,
        nombre: compromiso.nombre,
        descripcion: compromiso.descripcion || '',
        iniciativaId: compromiso.iniciativaId,
        tagIds: [...compromiso.tagIds],
      });
      // Cargar items existentes (de detail.hitos)
      const compHitos = detail.hitos.filter(
        (h) => h.parentType === 'compromiso' && h.parentId === compromiso.id,
      );
      setItems(
        compHitos.map((h) => ({
          id: h.id,
          titulo: h.titulo,
          original: h,
          isNew: false,
          archived: false,
        })),
      );
    } else {
      setForm(emptyForm(detail.iniciativas));
      setItems([]);
    }
  }, [open, compromiso, detail.iniciativas, detail.hitos]);

  // ── Mutations ───────────────────────────────────────────────────────────

  const mCreate = useMutation({
    mutationFn: async () => {
      const created = await createCompromiso({
        codigo: form.codigo,
        nombre: form.nombre,
        descripcion: form.descripcion || undefined,
        iniciativaId: form.iniciativaId,
        tagIds: form.tagIds,
        items: items
          .filter((it) => it.isNew && it.titulo.trim())
          .map((it) => ({ titulo: it.titulo.trim() })),
      });
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      onClose();
    },
    onError: (e: any) => setError(parseError(e)),
  });

  const mUpdate = useMutation({
    mutationFn: async () => {
      if (!compromiso) throw new Error('No hay partida');
      // 1) Update del compromiso (campos + tags)
      await updateCompromiso(compromiso.id, {
        codigo: form.codigo,
        nombre: form.nombre,
        descripcion: form.descripcion || null,
        iniciativaId: form.iniciativaId,
        tagIds: form.tagIds,
      });

      // 2) Crear items nuevos
      const nuevos = items.filter((it) => it.isNew && it.titulo.trim());
      for (const it of nuevos) {
        await createHito({
          parentType: 'compromiso',
          parentId: compromiso.id,
          titulo: it.titulo.trim(),
        });
      }

      // 3) Editar títulos cambiados
      const editados = items.filter(
        (it) => !it.isNew && it.original && it.titulo.trim() && it.titulo !== it.original.titulo,
      );
      for (const it of editados) {
        await editHito(it.id, { titulo: it.titulo.trim() });
      }

      // 4) Archivar items marcados (que existían y se marcaron archivar)
      const archivar = items.filter((it) => !it.isNew && it.archived && it.original);
      for (const it of archivar) {
        await archiveHito(it.id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      onClose();
    },
    onError: (e: any) => setError(parseError(e)),
  });

  const mArchive = useMutation({
    mutationFn: async () => {
      if (!compromiso) return;
      await archiveCompromiso(compromiso.id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setConfirmArchive(false);
      onClose();
    },
    onError: (e: any) => setError(parseError(e)),
  });

  const mRestoreItem = useMutation({
    mutationFn: (id: string) => restoreHito(id),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  // ── Tag creation inline ────────────────────────────────────────────────

  const [creatingTag, setCreatingTag] = useState(false);
  const [newTagNombre, setNewTagNombre] = useState('');
  const mCreateTag = useMutation({
    mutationFn: () =>
      createTag({ categoria: 'general', nombre: newTagNombre.trim() }),
    onSuccess: (tag) => {
      qc.invalidateQueries({ queryKey: ['tags'] });
      setForm((f) => ({ ...f, tagIds: [...f.tagIds, tag.id] }));
      setCreatingTag(false);
      setNewTagNombre('');
    },
    onError: (e: any) => setError(parseError(e)),
  });

  // ── Eventos (historial) ────────────────────────────────────────────────

  const { data: eventos } = useQuery({
    queryKey: ['compromiso-eventos', compromiso?.id],
    queryFn: () => listCompromisoEventos(compromiso!.id),
    enabled: !!compromiso && tab === 'historial',
  });

  const isSubmitting = mCreate.isPending || mUpdate.isPending;
  const formIsValid =
    form.codigo.trim() && form.nombre.trim() && form.iniciativaId;

  const tagsById = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);
  const tagsDisponibles = useMemo(
    () => tags.filter((t) => !form.tagIds.includes(t.id)),
    [tags, form.tagIds],
  );

  if (!open) return null;

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-drawer-title"
      >
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <div className={styles.codigo}>
              {isEdit ? 'Editar partida' : 'Nueva partida'}
            </div>
            <h2 id="edit-drawer-title">
              {isEdit ? `${compromiso!.codigo} · ${compromiso!.nombre}` : 'Crear partida'}
            </h2>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${tab === 'datos' ? styles.tabActive : ''}`}
            onClick={() => setTab('datos')}
          >
            Datos
          </button>
          <button
            className={`${styles.tab} ${tab === 'items' ? styles.tabActive : ''}`}
            onClick={() => setTab('items')}
          >
            Ítems · {items.filter((i) => !i.archived).length}
          </button>
          {isEdit && (
            <button
              className={`${styles.tab} ${tab === 'historial' ? styles.tabActive : ''}`}
              onClick={() => setTab('historial')}
            >
              Historial
            </button>
          )}
        </div>

        <div className={styles.body}>
          {tab === 'datos' && (
            <DatosTab
              form={form}
              setForm={setForm}
              iniciativas={detail.iniciativas}
              tags={tags}
              tagsById={tagsById}
              tagsDisponibles={tagsDisponibles}
              creatingTag={creatingTag}
              setCreatingTag={setCreatingTag}
              newTagNombre={newTagNombre}
              setNewTagNombre={setNewTagNombre}
              onCreateTag={() => mCreateTag.mutate()}
              creatingTagPending={mCreateTag.isPending}
            />
          )}
          {tab === 'items' && (
            <ItemsTab
              items={items}
              setItems={setItems}
              archivadosVisibles={archivadosVisibles}
              setArchivadosVisibles={setArchivadosVisibles}
              detailHitos={detail.hitos}
              compromisoId={compromiso?.id}
              onRestoreItem={(id) => mRestoreItem.mutate(id)}
            />
          )}
          {tab === 'historial' && (
            <HistorialTab eventos={eventos || []} />
          )}
        </div>

        {error && (
          <div style={{ padding: '8px 22px 0', color: 'var(--st-block)', fontSize: 12 }}>
            ⚠ {error}
          </div>
        )}

        <div className={styles.footer}>
          {isEdit ? (
            <button
              type="button"
              className={styles.btnDanger}
              onClick={() => setConfirmArchive(true)}
              disabled={isSubmitting || mArchive.isPending}
            >
              🗄 Archivar partida
            </button>
          ) : (
            <span />
          )}
          <div className={styles.footerRight}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              disabled={isSubmitting || !formIsValid}
              onClick={() => {
                setError(null);
                if (isEdit) mUpdate.mutate();
                else mCreate.mutate();
              }}
            >
              {isSubmitting
                ? 'Guardando…'
                : isEdit
                  ? 'Guardar cambios'
                  : 'Crear partida'}
            </button>
          </div>
        </div>
      </aside>

      <ConfirmArchiveModal
        open={confirmArchive}
        nombre={compromiso ? `${compromiso.codigo} · ${compromiso.nombre}` : ''}
        itemsCount={items.filter((i) => !i.archived && !i.isNew).length}
        onCancel={() => setConfirmArchive(false)}
        onConfirm={() => mArchive.mutate()}
        submitting={mArchive.isPending}
      />
    </>
  );
}

// ── Sub-componentes ──────────────────────────────────────────────────────

interface EditableItem {
  id: string; // id real si !isNew, temp si isNew
  titulo: string;
  original?: Hito;
  isNew: boolean;
  archived: boolean;
}

function DatosTab(props: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  iniciativas: Iniciativa[];
  tags: Tag[];
  tagsById: Map<string, Tag>;
  tagsDisponibles: Tag[];
  creatingTag: boolean;
  setCreatingTag: (v: boolean) => void;
  newTagNombre: string;
  setNewTagNombre: (v: string) => void;
  onCreateTag: () => void;
  creatingTagPending: boolean;
}) {
  const { form, setForm, iniciativas, tagsById, tagsDisponibles } = props;
  return (
    <>
      <div className={styles.field}>
        <label className={styles.fieldLabel}>Código *</label>
        <input
          className={styles.input}
          value={form.codigo}
          maxLength={32}
          onChange={(e) => setForm((f) => ({ ...f, codigo: e.target.value }))}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Nombre *</label>
        <input
          className={styles.input}
          value={form.nombre}
          maxLength={255}
          onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Iniciativa *</label>
        <select
          className={styles.select}
          value={form.iniciativaId}
          onChange={(e) => setForm((f) => ({ ...f, iniciativaId: e.target.value }))}
        >
          {iniciativas.map((i) => (
            <option key={i.id} value={i.id}>
              {i.codigo} · {i.nombre}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Descripción</label>
        <textarea
          className={styles.textarea}
          rows={4}
          maxLength={5000}
          value={form.descripcion}
          onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Tags</label>
        <div className={styles.tagsWrap}>
          {form.tagIds.map((tid) => {
            const t = tagsById.get(tid);
            if (!t) return null;
            return (
              <span
                key={tid}
                className={styles.tagChip}
                style={{ background: t.color + '22', color: t.color }}
              >
                {t.nombre}
                <button
                  type="button"
                  className={styles.tagChipRemove}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      tagIds: f.tagIds.filter((id) => id !== tid),
                    }))
                  }
                  aria-label={`Quitar ${t.nombre}`}
                >
                  ✕
                </button>
              </span>
            );
          })}
          {form.tagIds.length === 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-soft)' }}>Sin tags</span>
          )}
        </div>

        <div className={styles.tagAddRow}>
          {tagsDisponibles.length > 0 && (
            <select
              className={styles.select}
              style={{ flex: 1, maxWidth: 240 }}
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  setForm((f) => ({ ...f, tagIds: [...f.tagIds, e.target.value] }));
                }
              }}
            >
              <option value="">+ agregar tag…</option>
              {tagsDisponibles.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </select>
          )}
          {!props.creatingTag ? (
            <button
              type="button"
              className={styles.tagCreateBtn}
              onClick={() => props.setCreatingTag(true)}
            >
              + crear tag nuevo
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                className={styles.input}
                style={{ width: 180 }}
                placeholder="Nombre del tag"
                autoFocus
                value={props.newTagNombre}
                onChange={(e) => props.setNewTagNombre(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && props.newTagNombre.trim()) {
                    props.onCreateTag();
                  } else if (e.key === 'Escape') {
                    props.setCreatingTag(false);
                    props.setNewTagNombre('');
                  }
                }}
              />
              <button
                type="button"
                className={styles.btnPrimary}
                style={{ padding: '4px 10px', fontSize: 12 }}
                disabled={props.creatingTagPending || !props.newTagNombre.trim()}
                onClick={props.onCreateTag}
              >
                ✓
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={() => {
                  props.setCreatingTag(false);
                  props.setNewTagNombre('');
                }}
              >
                ✕
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ItemsTab(props: {
  items: EditableItem[];
  setItems: React.Dispatch<React.SetStateAction<EditableItem[]>>;
  archivadosVisibles: boolean;
  setArchivadosVisibles: (v: boolean) => void;
  detailHitos: Hito[];
  compromisoId?: string;
  onRestoreItem: (id: string) => void;
}) {
  const { items, setItems } = props;
  const [nuevoTitulo, setNuevoTitulo] = useState('');

  // Items archivados existentes (vienen aparte porque detail.hitos no los incluye)
  // Aquí mostraremos opción genérica; la carga real se hace via listArchivados afuera.

  const activos = items.filter((i) => !i.archived);
  const archivadosLocales = items.filter((i) => i.archived);

  function addItem() {
    if (!nuevoTitulo.trim()) return;
    setItems((prev) => [
      ...prev,
      {
        id: `tmp-${Date.now()}-${Math.random()}`,
        titulo: nuevoTitulo.trim(),
        isNew: true,
        archived: false,
      },
    ]);
    setNuevoTitulo('');
  }

  return (
    <>
      <div className={styles.itemList}>
        {activos.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-soft)', padding: '8px 0' }}>
            Sin ítems. Agrega el primero abajo.
          </div>
        )}
        {activos.map((it) => (
          <div key={it.id} className={styles.itemRow}>
            <input
              className={styles.itemTituloInput}
              value={it.titulo}
              onChange={(e) =>
                setItems((prev) =>
                  prev.map((p) =>
                    p.id === it.id ? { ...p, titulo: e.target.value } : p,
                  ),
                )
              }
            />
            <button
              type="button"
              className={`${styles.itemIconBtn} ${styles.itemIconBtnDanger}`}
              onClick={() => {
                if (it.isNew) {
                  // remover de la lista
                  setItems((prev) => prev.filter((p) => p.id !== it.id));
                } else {
                  // marcar para archivar al guardar
                  setItems((prev) =>
                    prev.map((p) => (p.id === it.id ? { ...p, archived: true } : p)),
                  );
                }
              }}
              aria-label="Archivar ítem"
              title={it.isNew ? 'Quitar' : 'Archivar al guardar'}
            >
              🗄
            </button>
          </div>
        ))}
      </div>

      <div className={styles.addItemRow}>
        <input
          className={styles.input}
          placeholder="Agregar nuevo ítem…"
          value={nuevoTitulo}
          onChange={(e) => setNuevoTitulo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addItem();
          }}
        />
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={addItem}
          disabled={!nuevoTitulo.trim()}
        >
          +
        </button>
      </div>

      {archivadosLocales.length > 0 && (
        <>
          <button
            type="button"
            className={styles.archivadosToggle}
            onClick={() => props.setArchivadosVisibles(!props.archivadosVisibles)}
          >
            {props.archivadosVisibles ? '▾' : '▸'} A archivar al guardar (
            {archivadosLocales.length})
          </button>
          {props.archivadosVisibles && (
            <div className={styles.itemList} style={{ marginTop: 8 }}>
              {archivadosLocales.map((it) => (
                <div key={it.id} className={`${styles.itemRow} ${styles.itemRowArchivado}`}>
                  <span className={styles.itemTitulo}>{it.titulo}</span>
                  <button
                    type="button"
                    className={styles.itemIconBtn}
                    onClick={() =>
                      setItems((prev) =>
                        prev.map((p) =>
                          p.id === it.id ? { ...p, archived: false } : p,
                        ),
                      )
                    }
                    aria-label="Cancelar archivado"
                    title="Cancelar archivado"
                  >
                    ↺
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

function HistorialTab({ eventos }: { eventos: EntidadEvento[] }) {
  if (eventos.length === 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text-soft)', padding: '8px 0' }}>
        Sin historial todavía.
      </div>
    );
  }
  return (
    <div>
      {eventos.map((e) => (
        <EventoRow key={e.id} evento={e} />
      ))}
    </div>
  );
}

function EventoRow({ evento }: { evento: EntidadEvento }) {
  const fecha = new Date(evento.createdAt).toLocaleString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const meta = eventoMeta(evento);
  return (
    <div className={styles.eventoRow}>
      <div
        className={styles.eventoIcon}
        style={{ background: meta.bg, color: meta.color }}
      >
        {meta.icon}
      </div>
      <div className={styles.eventoBody}>
        <div className={styles.eventoMeta}>
          {fecha}
          {evento.hitoTitulo && ` · ítem: ${evento.hitoTitulo}`}
        </div>
        <div className={styles.eventoTitulo}>{meta.titulo}</div>
        {evento.comentario && (
          <div className={styles.eventoComentario}>{evento.comentario}</div>
        )}
      </div>
    </div>
  );
}

function eventoMeta(e: EntidadEvento): {
  icon: string;
  titulo: string;
  bg: string;
  color: string;
} {
  if (e.tipo === 'cambio_estado') {
    return {
      icon: '🔁',
      titulo: `Estado: ${e.estadoAnterior ?? '—'} → ${e.estadoNuevo ?? '—'}`,
      bg: 'rgba(99,102,241,0.15)',
      color: '#4f46e5',
    };
  }
  if (e.tipo === 'creacion') {
    return {
      icon: '✚',
      titulo: e.parentType === 'compromiso' ? 'Partida creada' : 'Ítem creado',
      bg: 'rgba(34,197,94,0.15)',
      color: '#16a34a',
    };
  }
  if (e.tipo === 'eliminacion') {
    return {
      icon: '🗄',
      titulo:
        e.parentType === 'compromiso' ? 'Partida archivada' : 'Ítem archivado',
      bg: 'rgba(239,68,68,0.15)',
      color: '#dc2626',
    };
  }
  if (e.tipo === 'restauracion') {
    return {
      icon: '↺',
      titulo:
        e.parentType === 'compromiso' ? 'Partida restaurada' : 'Ítem restaurado',
      bg: 'rgba(168,85,247,0.15)',
      color: '#9333ea',
    };
  }
  // edicion
  return {
    icon: '✎',
    titulo: `Editado: ${e.campo} (${truncate(e.valorAnterior)} → ${truncate(e.valorNuevo)})`,
    bg: 'rgba(245,158,11,0.15)',
    color: '#d97706',
  };
}

function truncate(s: string | null, n = 30): string {
  if (s == null) return '—';
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
}

function emptyForm(inis: Iniciativa[]): FormState {
  return {
    codigo: '',
    nombre: '',
    descripcion: '',
    iniciativaId: inis[0]?.id ?? '',
    tagIds: [],
  };
}

function parseError(e: any): string {
  return (
    e?.response?.data?.message ||
    (Array.isArray(e?.response?.data?.message) && e.response.data.message[0]) ||
    e?.message ||
    'Error desconocido'
  );
}
